import job from "./mappings/job.json";
import type { AdapterApplyAnswers, AdapterConnectionDefinition, AdapterDefinition, AdapterDeltaOutputField, AdapterEntitySelection, AdapterFileProcessingRouteDefinition, AdapterMappingDefinition, AdapterMappingGuidance, AdapterOnboardingDefinition, AdapterQuestion, AdapterScenarioFile, AdapterStepBlock, AdapterWorkflowStepDefinition } from "../../types";
import awardInterpretationGuidanceData from "./mappings/award-interpretation.guidance.json";
import awardInterpretation from "./mappings/award-interpretation.json";
import contactToSession from "./mappings/contact-to-session.json";
import contactToSessionGuidanceData from "./mappings/contact-to-session.guidance.json";
import contactGuidanceData from "./mappings/contact.guidance.json";
import contact from "./mappings/contact.json";
import jobGuidanceData from "./mappings/job.guidance.json";
import orgmodel from "./mappings/orgmodel.json";
import orgmodelGuidanceData from "./mappings/orgmodel.guidance.json";
import payOutcome from "./mappings/pay-outcome.json";
import payOutcomeGuidanceData from "./mappings/pay-outcome.guidance.json";
import payOutcomeNormalization from "./mappings/pay-outcome-normalization.json";
import { withDeltaOutputFields } from "../../deltaOutputFields";
import { withExtensionTemplateColumns } from "../../extensionTemplateColumns";
import payOutcomeNormalizationGuidanceData from "./mappings/pay-outcome-normalization.guidance.json";
import leave from "./mappings/leave.json";
import leaveGuidanceData from "./mappings/leave.guidance.json";

import workPatternGuidanceData from "./mappings/work-pattern.guidance.json";
import workPattern from "./mappings/work-pattern.json";

import introductionContentRaw from "./content/introduction.md?raw";
import setupContentRaw from "./content/setup.md?raw";
import connectionContentRaw from "./content/connection.md?raw";
import routingContentRaw from "./content/routing.md?raw";
import fileProcessingContentRaw from "./content/file-processing.md?raw";
import alertsContentRaw from "./content/alerts.md?raw";
import finishContentRaw from "./content/finish.md?raw";
import edwardConfigContentRaw from "./content/edward-config.md?raw";
import { pageLink } from "../../../pageLinks";

/** Step copy lives in ./content as markdown; trailing whitespace from the files is dropped. */
const introductionContent = introductionContentRaw.trim();
const setupContent = setupContentRaw.trim();
const connectionContent = connectionContentRaw.trim();
const routingContent = routingContentRaw.trim();
const fileProcessingContent = fileProcessingContentRaw.trim();
const alertsContent = alertsContentRaw.trim();
const finishContent = finishContentRaw.trim();
const edwardConfigContent = edwardConfigContentRaw.trim();


/** A step's introduction. Without text the main block's default introduction is shown. */
const contentBlocks = (content: string | undefined): AdapterStepBlock[] =>
  content ? [{ kind: "content", content }] : [];
const contactGuidance = contactGuidanceData as AdapterMappingGuidance[];
const contactToSessionGuidance = contactToSessionGuidanceData as AdapterMappingGuidance[];
const awardInterpretationGuidance =
  awardInterpretationGuidanceData as AdapterMappingGuidance[];
const jobGuidance = jobGuidanceData as AdapterMappingGuidance[];
const workPatternGuidance = workPatternGuidanceData as AdapterMappingGuidance[];
const payOutcomeNormalizationGuidance =
  payOutcomeNormalizationGuidanceData as AdapterMappingGuidance[];
const payOutcomeGuidance = payOutcomeGuidanceData as AdapterMappingGuidance[];

/**
 * The normalized pay record the delta mapping emits for each changed pay-record row. The pay outcome CSV reads
 * these as `@delta/<key>`, and the delta mapping's zero-amount filter reads `amount`.
 */
export const payOutcomeOutputFields: AdapterDeltaOutputField[] = [
  { key: "employeeId", label: "Employee ID", required: true, dataType: "Text attribute", exampleValue: "the pay record employee ID", instructions: "The pay-record field identifying the employee. Fills the EmployeeId column." },
  { key: "positionCode", label: "Position code", required: true, dataType: "Text attribute", exampleValue: "the pay record position ID", instructions: "The pay-record field identifying the position. Fills the PositionCode column." },
  { key: "date", label: "Date", required: true, dataType: "Date attribute", exampleValue: "the pay record date", instructions: "The pay-record field with the date the outcome applies to. Fills the EntryDate column." },
  { key: "code", label: "Pay component code", required: true, dataType: "Text attribute", exampleValue: "the pay record extended code", instructions: "The pay-record field with the Tech1 pay-component code. Fills the PayComponentCode column." },
  { key: "unit", label: "Units", required: true, dataType: "Number attribute", exampleValue: "the pay record units", instructions: "The pay-record field with the calculated units, including negative adjustments. Fills the Units column." },
  { key: "amount", label: "Amount", required: true, dataType: "Number attribute", exampleValue: "the pay record amount", instructions: "The pay-record field with the calculated amount. Zero-amount rows are filtered out using this field." },
  { key: "comment", label: "Comment", dataType: "Text attribute", exampleValue: "the pay record description", instructions: "Optionally, the pay-record field describing the outcome. Fills the Comments column; leave it unset for blank comments." },
];
const leaveGuidance = leaveGuidanceData as AdapterMappingGuidance[];
const orgmodelGuidance = orgmodelGuidanceData as AdapterMappingGuidance[];

const tech1Entities: AdapterEntitySelection[] = [
    { key: "entities.employee", label: "Employee", icon: "users", helpText: "The entity that stores employee details.", required: true },
    { key: "entities.assignment", label: "Assignment", icon: "briefcase", helpText: "The entity that holds each employee assignment.", required: true },
    { key: "entities.position", label: "Position", icon: "id-card", helpText: "The entity nested beneath an assignment for each position.", required: true },
    { key: "entities.workPattern", label: "Work pattern", icon: "calendar-range", helpText: "The entity that stores each effective-dated work pattern.", required: true },
    { key: "entities.workPatternDay", label: "Work pattern day", icon: "calendar-days", helpText: "The entity nested beneath a work pattern for its daily entries.", required: true },
    { key: "entities.timeEntry", label: "Time entry", icon: "clock", helpText: "The entity that receives imported award-interpretation time entries.", required: true, showWhen: { mode: "awardInterpretation" } },
    { key: "entities.payRecord", label: "Pay-record child entity", icon: "coins", placeholder: "Select the pay-record child entity", helpText: "The direct child entity containing calculated pay records for export, as named in Edward pay-run settings.", required: true },
  ];

const tech1Onboarding: AdapterOnboardingDefinition = {
    introduction: introductionContent,
    verification: [],
    stepLabels: {
      introduction: "About this setup",
      setup: "Choose where timesheets are managed",
      connection: "Set up outbound SFTP",
      routing: "Connect inbound files",
      fileProcessing: "Enable file processing",
      trigger: "Set up pay outcome delivery",
      verification: "Set up processing failure alerts",
      finish: "Test the setup end to end",
    },
    stepContent: {
      setup: setupContent,
      connection: connectionContent,
      routing: routingContent,
      fileProcessing: fileProcessingContent,
      verification: alertsContent,
      finish: finishContent,
    },
    verificationWarningTitle: "Confirm what you have tested",
    verificationWarning:
      "Do not confirm a check based only on saved configuration. You must see the expected result.",
    readiness: {
      configured: {
        title: "Configured",
        description: "Mappings, file routes, and delivery settings are ready.",
      },
      validated: {
        title: "Validated",
        description:
          "A test employee has been imported, their timesheet approved in Edward, and a pay run created.",
      },
      productionReady: {
        title: "Production-ready",
        description:
          "The setup is configured and has been tested end to end.",
      },
    },
  };

const tech1Questions: AdapterQuestion[] = [
    {
      key: "mode",
      label: "Where are timesheets managed?",
      helpText: "Choose the system that creates, submits, and approves the source timesheets.",
      type: "select",
      required: true,
      options: [
        {
          value: "timesheet",
          label: "Edward-managed timesheets",
          description: "Employees submit timesheets in Edward and managers approve them there.",
          details: `**How this pathway works:**

- Edward creates, submits, and approves the timesheets
- Decisively applies the existing release rules to calculate pay
- Approved results enter controlled pay runs
- The adapter uses the existing Edward session flow instead of adding Technology1 raw-time session resources`,
        },
        {
          value: "awardInterpretation",
          label: "Technology1-managed approved time",
          description:
            "Technology1 sends approved raw time; Decisively interprets each employee group before pay-run collection.",
          details: `**How this pathway works:**

- Technology1 supplies approved raw-time records
- CSV rows are grouped by Employee_ID; each valid group creates a separate Decisively session
- Decisively applies the selected release goal and rules to calculate pay
- The adapter adds the raw-time session mappings and pay-delta processing used by this pathway`,
        },
      ],
    },

  ];

const tech1Connections: AdapterConnectionDefinition[] = [{
    key: "outboundSftp",
    name: "Tech1 outbound SFTP",
    type: "sftp",
    identifier: "tech1-outbound",
    timeout: 10000,
    data: { host: "", username: "", port: 22, path: "./payruns", authType: "none" },
    bindings: ["host", "port", "path", "username"].map((key) => ({ sourcePath: key, targetPath: key })),
    guidance: [
      { id: "connection:host", targetPath: "host", instructions: "Use the hostname supplied by the Tech1 integration administrator.", requirements: [{ path: "host", operator: "notBlank" }] },
      { id: "connection:port", targetPath: "port", instructions: "Use the port supplied with the outbound SFTP destination. SFTP normally uses port 22.", requirements: [{ path: "port", operator: "notBlank" }] },
      { id: "connection:path", targetPath: "path", instructions: "Use the remote directory supplied for pay-output files, for example ./payruns.", requirements: [{ path: "path", operator: "notBlank" }] },
      { id: "connection:username", targetPath: "username", instructions: "Use the dedicated account supplied for this outbound destination.", requirements: [{ path: "username", operator: "notBlank" }] },
      { id: "connection:password", targetPath: "password", instructions: "Enter the secret supplied for this outbound destination.", requirements: [{ path: "hasPassword", operator: "equals", value: true }] },
    ],
  }];

const tech1MappingDefinitions: AdapterMappingDefinition[] = [
    {
      key: "contact",
      name: "Tech1 Contact Import",
      purpose:
        "Creates or updates employee contacts from Tech1 and identifies each employee using the project's employee identifier attribute.",
      type: "contact",
      mode: "csv",
      data: contact,
      guidance: contactGuidance,
      bindings: ["tech1-contact-employee-id", "tech1-contact-work-email"].map((id) => ({ sourcePath: "entities.employee", targetPath: `columns[id=${id}].entity` })),
      sampleFiles: [
        {
          fileName: "EC_DEMO_SCENARIOS.csv",
          href: "/template-samples/tech1/EC_DEMO_SCENARIOS.csv",
          description: "Employee contacts from Tech1.",
        },
      ],
    },
    {
      key: "job",
      name: "Tech1 Job Import",
      purpose:
        "Creates and updates each employee's assignment and position records, including effective dates, position details, employee class, category, and classification.",
      type: "contact",
      mode: "csv",
      data: job,
      guidance: jobGuidance,
      bindings: [
        ...["tech1-job-assignment-id", "tech1-job-assignment", "tech1-job-assignment-from", "tech1-job-assignment-to"].flatMap((id) => [
          { sourcePath: "entities.assignment", targetPath: `columns[id=${id}].entity` },
          { sourcePath: "entities.assignment", targetPath: `columns[id=${id}].path`, valueTemplate: "{{answers.entities.assignment}}/{{AssignmentId}}" },
        ]),
        ...["tech1-job-effective-from", "tech1-job-effective-to", "tech1-job-occupancy-type", "tech1-job-position-position-id", "tech1-job-position-id", "tech1-job-category", "tech1-job-position-description", "tech1-job-employee-class", "tech1-job-holiday-category", "tech1-job-grade"].flatMap((id) => [
          { sourcePath: "entities.position", targetPath: `columns[id=${id}].entity` },
          { sourcePath: "entities.position", targetPath: `columns[id=${id}].path`, valueTemplate: "{{answers.entities.assignment}}/{{AssignmentId}}/{{answers.entities.position}}/{{Position}}" },
        ]),
      ],
      sampleFiles: [
        {
          fileName: "EP_DEMO_SCENARIOS.csv",
          href: "/template-samples/tech1/EP_DEMO_SCENARIOS.csv",
          description: "Employee positions from Tech1.",
        },
      ],
    },
    {
      key: "leave",
      name: "Tech1 Leave Import",
      purpose:
        "Loads Tech1 leave rows into the extensions used by timesheet and leave calculations.",
      type: "entities",
      mode: "csv",
      data: leave,
      guidance: leaveGuidance,
      bindings: [
        { sourcePath: "entities.assignment", targetPath: "data.path", valueTemplate: "{{answers.entities.assignment}}/{{Assignment}}" },
      ],
      sampleFiles: [
        {
          fileName: "LV_DEMO_SCENARIO.csv",
          href: "/template-samples/tech1/LV_DEMO_SCENARIO.csv",
          description: "Leave entries from Tech1.",
        },
      ],
    },
    {
      key: "workPattern",
      name: "Tech1 Work Pattern Import",
      purpose:
        "Attaches effective-dated scheduled hours and fortnight cycles to the correct employee assignment and position.",
      type: "contact",
      mode: "csv",
      data: workPattern,
      guidance: workPatternGuidance,
      bindings: [
        ...["tech1-work-pattern-effective-from", "tech1-work-pattern-effective-to", "tech1-work-pattern-cycle"].flatMap((id) => [
          { sourcePath: "entities.workPattern", targetPath: `columns[id=${id}].entity` },
          { sourcePath: "entities.workPattern", targetPath: `columns[id=${id}].path`, valueTemplate: "{{answers.entities.assignment}}/{{AssignmentId}}/{{answers.entities.position}}/{{Position}}/{{answers.entities.workPattern}}/{{DataType}}" },
        ]),
        ...["tech1-work-pattern-day-number", "tech1-work-pattern-units"].flatMap((id) => [
          { sourcePath: "entities.workPatternDay", targetPath: `columns[id=${id}].entity` },
          { sourcePath: "entities.workPatternDay", targetPath: `columns[id=${id}].path`, valueTemplate: "{{answers.entities.assignment}}/{{AssignmentId}}/{{answers.entities.position}}/{{Position}}/{{answers.entities.workPattern}}/{{DataType}}/{{answers.entities.workPatternDay}}/{{DayNumber}}" },
        ]),
      ],
      sampleFiles: [
        {
          fileName: "WP_BY_EMPLOYEE_Demo_SCENARIOS.csv",
          href: "/template-samples/tech1/WP_BY_EMPLOYEE_Demo_SCENARIOS.csv",
          description: "Work patterns from Tech1.",
        },
      ],
    },
    {
      key: "orgmodel",
      name: "Tech1 Org Model Import",
      purpose:
        "Imports the position hierarchy: which position supervises which. Timesheet approvals follow it.",
      type: "orgmodels",
      mode: "csv",
      data: orgmodel,
      guidance: orgmodelGuidance,
      sampleFiles: [
        {
          fileName: "POS_DEMO_SCENARIO.csv",
          href: "/template-samples/tech1/POS_DEMO_SCENARIO.csv",
          description: "Position reporting relationships from Tech1.",
        },
      ],
    },
    withDeltaOutputFields({
      key: "payOutcomeNormalization",
      name: "Tech1 Pay Outcome Delta Mapping",
      purpose:
        "Compares current and previous pay-transfer values and emits only changed, non-zero pay-code rows for export.",
      type: "delta",
      mode: "json",
      data: payOutcomeNormalization,
      guidance: payOutcomeNormalizationGuidance,
      outputFields: payOutcomeOutputFields,
      bindings: [{ sourcePath: "entities.payRecord", targetPath: "entity" }],
    }),
    {
      key: "payOutcome",
      name: "Tech1 Pay Outcome",
      purpose:
        "Formats pay outcomes into the Tech1 CSV delivered through outbound SFTP.",
      type: "response",
      mode: "csv",
      data: payOutcome,
      guidance: payOutcomeGuidance,
      bindings: [
        { sourcePath: "entities.position", targetPath: "entityMapping.entityLine" },
        ...["tech1-pay-outcome-employee-id", "tech1-pay-outcome-position-code"].map((id) => ({ sourcePath: "entities.position", targetPath: `columns[id=${id}].entity` })),
      ],
    },
    {
      key: "awardInterpretation",
      name: "Tech1 Time Entry Import",
      purpose:
        "Turns inbound Tech1 time-entry rows into award-interpretation sessions and maps them to the project's timesheet model.",
      type: "request",
      mode: "csv",
      data: awardInterpretation,
      guidance: awardInterpretationGuidance,
      bindings: [{ sourcePath: "entities.timeEntry", targetPath: "entityMapping.entityLine" }],
      showWhen: { mode: "awardInterpretation" },
    },
    {
      key: "contactToSession",
      name: "Tech1 Contact to Session",
      purpose:
        "Copies the imported employee identifier and nested job data into each award-interpretation session before rules run.",
      type: "request",
      mode: "json",
      data: contactToSession,
      guidance: contactToSessionGuidance,
      showWhen: { mode: "awardInterpretation" },
    },
  ];

/** Extension mappings read their CSV columns through their JSON template, so their column guidance checks it. */
const tech1Mappings = tech1MappingDefinitions.map(withExtensionTemplateColumns);

const tech1Routes: AdapterFileProcessingRouteDefinition[] = [
    { key: "positions", scope: "contacts", mappingKey: "job", match: "EP_*" },
    {
      key: "workPatterns",
      scope: "contacts",
      mappingKey: "workPattern",
      match: "WP_*",
    },
    {
      key: "contacts",
      scope: "contacts",
      mappingKey: "contact",
      isDefault: true,
    },
    {
      key: "leave",
      scope: "entities",
      mappingKey: "leave",
      isDefault: true,
    },
    {
      key: "orgmodels",
      scope: "orgmodels",
      mappingKey: "orgmodel",
      isDefault: true,
    },
    {
      key: "awardInterpretation",
      scope: "sessions",
      mappingKey: "awardInterpretation",
      isDefault: true,
      showWhen: { mode: "awardInterpretation" },
      sessionGoalQuestionKey: "awardSessionGoal",
      batching: {
        mode: "csv-group-by",
        groupBy: ["Employee_ID"],
        maxConcurrency: 1,
        delayMs: 10,
        failurePolicy: "fail-partition",
      },
      contactData: {
        enabled: true,
        contactColumn: "Employee_ID",
        path: "",
        mappingKey: "contactToSession",
        externalIdentifier: true,
        externalIdentifierNamespace: "system",
        externalIdentifierKey: "id",
      },
    },
  ];

const edwardContactConfig: AdapterStepBlock = {
  kind: "externalConfig",
  id: "edward-employee-contacts",
  product: "edward",
  required: true,
  title: "Edward Config",
  content: edwardConfigContent,
};

const divider: AdapterStepBlock = { kind: "divider" };

/**
 * The employee entity is the contact itself, so the contact import puts its attributes on the contact (where Edward
 * finds the employee's ID and email) rather than in a nested employee record. Its contact ID is the work email
 * attribute, the value the import also uses as the contact's ID.
 */
const employeeContactEntityBlock: AdapterStepBlock = {
  kind: "contactEntity",
  resources: [{
    kind: "contactEntity",
    resource: {
      key: "employeeContactEntity",
      name: "Employee contact entity",
      description: "The employee entity should be the contact entity, so imported employee details go on the contact.",
      entityAnswerKey: "entities.employee",
      contactId: { mappingKey: "contact", columnId: "tech1-contact-work-email" },
    },
  }],
};

const mappingName = (key: string): string => tech1Mappings.find((mapping) => mapping.key === key)?.name ?? key;
const payDeltaMappingName = mappingName("payOutcomeNormalization");
const payDeliveryMappingName = mappingName("payOutcome");
const payDeliveryConnectionName = tech1Connections.find((connection) => connection.key === "outboundSftp")?.name ?? "outboundSftp";

/**
 * Edward's pay run settings (Admin → Meta → Payruns) are set up alongside the three resources they use: the delta
 * mapping step turns pay runs on and maps pay outcomes, the pay outcome CSV step picks the delivery mapping, and the
 * outbound SFTP step picks the connection and checks everything is in place.
 */
const edwardPayrunOutcomesConfig: AdapterStepBlock = {
  kind: "externalConfig",
  id: "edward-payruns-outcomes",
  product: "edward",
  required: true,
  title: "Edward Config",
  content: `Edward builds pay runs from these pay records. In Edward, open **Admin → Meta → Payruns**:

1. Turn on **Enabled**.
2. Choose the **Mode** that suits the project: **Controlled** if payroll officers review and finalise each pay run before it is sent, or **Continuous** to send without review.
3. Leave **Cut Off Days** at its default unless the project needs another.
4. Under **Pay Outcomes**, set **Pay outcome structure** to **Child pay records**.
5. Set **Pay-period day entity** to the day entity, a direct child of the position.
6. Set **Pay-record child entity** to the entity chosen as the pay-record child entity in this adapter.
7. Map its **Code**, **Description**, **Amount** and **Units** attributes.
8. Set **Delta mapping** to **${payDeltaMappingName}**.
9. Save the settings.

The delivery mapping and connection are chosen in the next steps.`,
};

const edwardPayrunDeliveryMappingConfig: AdapterStepBlock = {
  kind: "externalConfig",
  id: "edward-payruns-delivery-mapping",
  product: "edward",
  required: true,
  title: "Edward Config",
  content: `Edward uses this mapping to turn each finalised pay run's changed pay rows into the Tech1 file. In Edward, open **Admin → Meta → Payruns**:

1. Set **Delivery mapping** to **${payDeliveryMappingName}**.
2. Save the settings.`,
};

const edwardPayrunCheckConfig: AdapterStepBlock = {
  kind: "externalConfig",
  id: "edward-payruns-check",
  product: "edward",
  required: true,
  title: "Edward Config",
  content: `In Edward, open **Admin → Meta → Payruns**:

1. Set **Payroll delivery connection** to **${payDeliveryConnectionName}**.
2. Save the settings.
3. Check the whole pay run setup is in place:
   - **Enabled** is on and a **Mode** is chosen.
   - **Pay Outcomes** uses **Child pay records**, with the pay-period day entity, the pay-record child entity and its code, description, amount and units attributes set.
   - **Delta mapping** is **${payDeltaMappingName}**.
   - **Delivery mapping** is **${payDeliveryMappingName}**.
   - **Payroll delivery connection** is **${payDeliveryConnectionName}**.
4. Check **How payrun mappings work** shows no step marked *Needs configuration*.`,
};

const edwardCheck = (id: string, title: string, content: string, required = false): AdapterStepBlock => ({
  kind: "externalConfig",
  id,
  product: "edward",
  title,
  content,
  ...(required ? { required } : {}),
});

/** Decisively's Approver Derivation: a position's members approve the positions under it. */
const approverDerivationBlock: AdapterStepBlock = {
  kind: "tenancySetting",
  resources: [{
    kind: "tenancySetting",
    resource: {
      key: "approverDerivation",
      name: "Approver Derivation",
      description: "Turns the hierarchy and memberships into approvers: whoever holds a position approves the positions directly under it. Without it, no one approves anything.",
      path: "orgModels.implyApprovers",
      value: true,
    },
  }],
};

/**
 * The end-to-end test's employee. They hold a test position and the supervisor position above it, so once Edward
 * records them as a member of both positions and Approver Derivation is on, they approve their own test-position
 * timesheet. Every detail is assigned at random once, and kept, so repeat runs don't collide.
 */
const testEmployee = {
  employeeId: "{{testEmployee.employeeId}}",
  position: "{{testEmployee.position}}",
  positionAssignment: "{{testEmployee.employeeId}}-1",
  supervisorAssignment: "{{testEmployee.employeeId}}-2",
  supervisorPosition: "{{testEmployee.supervisorPosition}}",
};

const pick = (values: string[]): string => values[Math.floor(Math.random() * values.length)];
/** Four digits without a leading zero. */
const fourDigits = (): string => String(1000 + Math.floor(Math.random() * 9000));
/** `me@example.com` tagged as `me+tag@example.com`: a distinct address that still reaches the same inbox. */
const plusAddress = (email: string, tag: string): string => {
  const at = email.lastIndexOf("@");
  return at > 0 && tag ? `${email.slice(0, at)}+${tag}${email.slice(at)}` : email;
};
const testAnswer = (answers: AdapterApplyAnswers, key: string): string =>
  String((answers.testEmployee as Record<string, unknown> | undefined)?.[key] ?? "");
/** The test run's number: the employee ID's digits (kept if one was assigned before), or four new ones. */
const testNumber = (answers: AdapterApplyAnswers): string =>
  testAnswer(answers, "employeeId").match(/^EMP(\d{4})$/)?.[1] ?? fourDigits();

/** The test employee's details: assigned once, then kept with the other answers. */
const testEmployeeQuestions: AdapterQuestion[] = [
  {
    key: "testEmployee.number",
    label: "Test number",
    helpText: "",
    type: "text",
    required: true,
    hidden: true,
    prefill: ({ answers }) => testNumber(answers),
  },
  {
    key: "testEmployee.firstName",
    label: "First name",
    helpText: "",
    type: "text",
    required: true,
    readOnly: true,
    prefill: ({ user }) => user.firstName || pick(["Alex", "Sam", "Jordan", "Taylor", "Casey", "Riley", "Morgan", "Jamie"]),
  },
  {
    key: "testEmployee.lastName",
    label: "Last name",
    helpText: "",
    type: "text",
    required: true,
    readOnly: true,
    prefill: ({ user }) => user.lastName || "Tester",
  },
  {
    key: "testEmployee.employeeId",
    label: "Employee ID",
    helpText: "",
    type: "text",
    required: true,
    readOnly: true,
    prefill: ({ answers }) => `EMP${testAnswer(answers, "number")}`,
  },
  {
    key: "testEmployee.workEmail",
    label: "Work email",
    helpText: "",
    type: "text",
    required: true,
    readOnly: true,
    prefill: ({ answers, user }) => plusAddress(user.email || "test@example.com", testAnswer(answers, "employeeId")),
  },
  {
    key: "testEmployee.position",
    label: "Test position code",
    helpText: "",
    type: "text",
    required: true,
    readOnly: true,
    prefill: () => `P${fourDigits()}`,
  },
  {
    key: "testEmployee.supervisorPosition",
    label: "Supervisor position code",
    helpText: "",
    type: "text",
    required: true,
    readOnly: true,
    prefill: ({ answers }) => {
      let code = `P${fourDigits()}`;
      while (code === testAnswer(answers, "position")) code = `P${fourDigits()}`;
      return code;
    },
  },
];

const testJob = (assignmentId: string, position: string, occupancyType: string, description: string) => ({
  DataType: "EP",
  EmployeeID: testEmployee.employeeId,
  AssignmentId: assignmentId,
  AssignmentFrom: "01-Jan-2026",
  AssignmentTo: "01-Dec-2027",
  EffectiveFrom: "01-Jan-2026",
  EffectiveTo: "01-Dec-2027",
  OccupancyType: occupancyType,
  Position: position,
  Category: "Teacher",
  Classification: "T2-6 JUL25",
  PositionDescription: description,
  EmployeeType: "P",
  EmployeeTypeDescr: "Permanent",
  EmployeeClass: "P",
  EmployeeClassDescr: "Part Time",
  PayGroup: "CEMEA2022",
  PayGroupDescr: "Catholic Education Multi Enterprise Agreement 2022",
  PayCalcMethod: "E",
  HolidayCategory: "VIC",
  HolidayCategoryDescr: "Victoria",
  Company: "1",
  FTE: "0.8",
  OrganisationUnit: "PR",
  OrganisationUnitDescr: "Primary school",
  WorkGroup: "School",
  WorkLocation: "School",
  Condition: "CEMEA2022",
  ConditionDescr: "CEMEA 2022",
  Grade: "T2-6 JUL25",
  GradeDescr: "T2-6 JUL25",
  Level: "",
  LevelDescr: "",
  Cohort: "NS",
  CohortDescr: "Cohort Not Specified",
  JPSelectionCode1: "GRG",
  JPSelectionCode2: "SALARY_94450.40",
  JPSelectionCode3: "SRC_POS_205",
});

/** A fortnightly 0.8 FTE pattern: 7.6 hours Monday to Thursday, the rest of each week off. */
const testWorkPattern = Array.from({ length: 14 }, (_, day) => {
  const working = day % 7 < 4;
  return {
    DataType: "WP",
    EmployeeID: testEmployee.employeeId,
    Position: testEmployee.position,
    Company: "1",
    AssignmentId: testEmployee.positionAssignment,
    EffectiveFrom: "01-Jan-2026",
    EffectiveTo: "01-Dec-2027",
    DayNumber: String(day),
    RosterCode: "0.8_07D_CESL",
    ShiftCode: working ? "7.6" : "OFF",
    Clock_In_Time: working ? "1900-01-01 9:00:00" : "",
    Clock_Out_Time: working ? "1900-01-01 16:36:00" : "",
    Units: working ? "7.6" : "0",
  };
});

const testFileName = (prefix: string) => `${prefix}_TEST_SCENARIO_{{testEmployee.number}}.csv`;

/** The test-only files, one per import, each holding just the test employee. */
const testFiles: AdapterScenarioFile[] = [
  {
    mappingKey: "contact",
    fileName: testFileName("EC"),
    rows: [{
      DataType: "EC",
      EmployeeID: testEmployee.employeeId,
      LastName: "{{testEmployee.lastName}}",
      FirstName: "{{testEmployee.firstName}}",
      UserName: "",
      WorkEmail: "{{testEmployee.workEmail}}",
      Status: "Active",
      Company: "1",
    }],
  },
  {
    mappingKey: "job",
    fileName: testFileName("EP"),
    rows: [
      testJob(testEmployee.positionAssignment, testEmployee.position, "Substantive Position", "Test Position"),
      testJob(testEmployee.supervisorAssignment, testEmployee.supervisorPosition, "Higher Duties", "Test Supervisor"),
    ],
  },
  { mappingKey: "workPattern", fileName: testFileName("WP"), rows: testWorkPattern },
  {
    mappingKey: "orgmodel",
    fileName: testFileName("POS"),
    rows: [{
      DataType: "POS",
      UniqueKey: `TEST_${testEmployee.position}`,
      Company: "1",
      Position: testEmployee.position,
      PositionDescription: "Test Position",
      Status: "Active",
      EffectiveFrom: "1-Jan-26",
      // A real end date rather than Tech1's 1-Jan-00 "no end", so the range reads right whatever the mapping's end-of-time setting.
      EffectiveTo: "31-Dec-27",
      AuthorisedApprover: "N",
      SupervisorPositionCode: testEmployee.supervisorPosition,
    }],
  },
];

/**
 * The final page: a test employee through the whole integration, from test-only files that hold just them. The imports
 * are checked off by hand; the Edward steps are checked off with their own Done boxes.
 */
const endToEndTestBlocks: AdapterStepBlock[] = [
  ...contentBlocks(tech1Onboarding.stepContent.finish),
  divider,
  ...contentBlocks(`#### The test employee

The files below create this test employee. You'll sign in to Edward as them with their work email, and its emails come to your inbox. Generate new details to start a fresh run, then download the files again.`),
  { kind: "questions", questions: testEmployeeQuestions },
  ...contentBlocks(`Download the files for them. Columns your mappings add get example values.`),
  { kind: "scenarioFiles", files: testFiles },
  divider,
  ...contentBlocks(`#### 1. Import the employee

Upload **${testFileName("EC")}** to the **contacts** staging folder.`),
  { kind: "verification", items: [{ id: "e2e-employee-imported", title: "The test employee's contact was created", description: "Contacts has the test employee with their work email." }] },
  divider,
  ...contentBlocks(`#### 2. Import their jobs

The test employee gets two jobs: the test position, and the supervisor position above it. Upload **${testFileName("EP")}** to the **contacts** staging folder; Edward then records them as a member of both positions.`),
  { kind: "verification", items: [{ id: "e2e-job-imported", title: "Their jobs were added", description: `Their contact has both positions, and ${pageLink("orgModels")} lists them as a member of both.` }] },
  divider,
  ...contentBlocks(`#### 3. Import their working hours

They work the test position. Upload **${testFileName("WP")}** to the **contacts** staging folder.`),
  { kind: "verification", items: [{ id: "e2e-work-hours-imported", title: "Their working hours were added", description: "The test position on their contact has the work pattern and its days." }] },
  divider,
  ...contentBlocks(`#### 4. Import the position hierarchy

This puts the test position under the supervisor position, so the test employee approves their own test-position timesheets. Upload **${testFileName("POS")}** to the **org models** staging folder.`),
  { kind: "verification", items: [{ id: "e2e-org-model-imported", title: "The test employee approves the test position", description: `${pageLink("orgModels")} shows a resolved approver for the test position pointing at the test employee. Choose **Rebuild derived org model rows** if it's missing.` }] },
  divider,
  ...contentBlocks(`#### 5. Submit, approve and pay in Edward

Tick each one's **Done** once it's worked.`),
  edwardCheck("edward-e2e-contact-object", "Give them a contact object", `Before a timesheet can be submitted, the test employee needs a contact object that suits your rule base: the attributes and entities your rules need to work out pay, such as employment type, award and pay details.

1. Open the test employee in **Contacts**.
2. Add the attributes and entities your rules need, here or through your imports.
3. Check the rules can evaluate them.`, true),
  edwardCheck("edward-e2e-timesheet", "Submit a timesheet", `1. Sign in to Edward with the test employee's work email.
2. Open **Timesheets**.
3. Create a timesheet for the current period on **Test Position**.
4. Enter some time and submit it.`, true),
  edwardCheck("edward-e2e-approval", "Approve the timesheet", `The approval task goes to the test employee, as the supervisor position's holder.

1. Select the bell icon to open the task list.
2. Find the approval task for the timesheet.
3. Approve it.`, true),
  edwardCheck("edward-e2e-payrun", "Check the pay run", `1. Open **Pay Runs**.
2. Check a pay run was created with the approved time and the pay outcomes you expect.
3. Only finalise and transfer it if the outbound connection points at a test Tech1 environment.`, true),
  // Completes the setup once every step and check above is done; the checks are the page's checklist, so it shows
  // nothing of its own.
  { kind: "finish", showSummary: false, verificationItems: [] },
];

/**
 * Approvals follow the position hierarchy: Edward records each employee as a member of their positions, and
 * Decisively's Approver Derivation makes a position's members approve the positions under it.
 */
const orgModelApprovalBlocks: AdapterStepBlock[] = [
  ...contentBlocks(`#### How approvals work

Edward sends each timesheet to whoever holds the position above the employee's position. The org model works that out from three kinds of record:

| Record | What it says | Where it comes from |
| --- | --- | --- |
| **Parent** | P9011 sits under P9012 | This import, from each row's **SupervisorPositionCode** |
| **Member** | Alex holds P9012 | Edward, from the positions the job import puts on each contact |
| **Approver** | Alex approves P9011 | Decisively, from the two above |

So the members of a position approve the positions directly under it. Holders of positions further up are kept as escalation approvers: the grandparent's holders at level 1, and so on.

#### Settings

Both are needed, and both should be on before you import jobs:`),
  approverDerivationBlock,
  edwardCheck("edward-org-model-supervisors", "Use the org model for approvals", `Without this, Edward doesn't record who holds each position, and doesn't look up approvers in the org model. In Edward, open **Admin → Meta → Config**:

1. Turn on **Supervisors**.
2. Set **Method** to **Org Models**.
3. Set **Subject** to **position**.
4. Set **Subject ID Attribute** to the attribute holding the position code.
5. Turn on **Infer membership through positions**, so Edward records each employee as a member of the positions on their contact.
6. Save the settings.`, true),
  divider,
  ...contentBlocks(`#### Keeping approvers up to date

Approvers are worked out again each time the position hierarchy is imported. Edward records memberships as jobs are imported, so after importing jobs, import the hierarchy again or choose **Rebuild derived org model rows** in ${pageLink("orgModels")}. That page also shows each position's parent, members and approvers, which is the place to check when a timesheet goes to the wrong person.`),
];

/** Blocks shown after a mapping step's mapping block, keyed by mapping key. */
const mappingStepExtraBlocks: Partial<Record<string, AdapterStepBlock[]>> = {
  contact: [edwardContactConfig],
  orgmodel: orgModelApprovalBlocks,
  payOutcomeNormalization: [edwardPayrunOutcomesConfig],
  payOutcome: [edwardPayrunDeliveryMappingConfig],
};

const mappingSteps: AdapterWorkflowStepDefinition[] = tech1Mappings.map((mapping) => {
  const extraBlocks = mappingStepExtraBlocks[mapping.key];
  return {
    key: `mapping:${mapping.key}`,
    blocks: [
      { kind: "mapping", resources: [{ kind: mapping.type === "delta" ? "deltaMapping" : "mapping", resource: mapping }] },
      ...(extraBlocks ? [divider, ...extraBlocks] : []),
    ],
    showWhen: mapping.showWhen,
    prerequisites: [{ stepKey: "entities", requirement: "complete" }],
  };
});

const connectionSteps: AdapterWorkflowStepDefinition[] = tech1Connections.map((connection) => ({
  key: `connection:${connection.key}`,
  label: "Set up outbound SFTP",
  blocks: [
    ...contentBlocks(connection.stepContent ?? tech1Onboarding.stepContent.connection),
    { kind: "connection", resources: [{ kind: "connection", resource: connection }] },
    ...(connection.key === "outboundSftp" ? [divider, edwardPayrunCheckConfig] : []),
  ],
  showWhen: connection.showWhen,
  prerequisites: [{
    stepKey: "mapping:payOutcome",
    requirement: "complete",
    reason: "Complete the pay outcome mapping before setting up outbound SFTP.",
  }],
}));

export const tech1AdapterDefinition: AdapterDefinition = {
  id: "tech1",
  nameQuestionKey: "mode",
  name: "Tech1",
  image: "/images/tech1-logo.png",
  version: "1.0.17",
  description: "Import Tech1 workforce data, collect controlled pay-run items, and deliver completed payroll files through outbound SFTP.",
  defaultAnswers: {
    authType: "none",
    port: 22,
    path: "./payruns",
  },
  workflow: [
    {
      key: "introduction",
      label: tech1Onboarding.stepLabels.introduction,
      requiresConfiguration: false,
      blocks: contentBlocks(tech1Onboarding.introduction),
    },
    {
      key: "setup",
      label: tech1Onboarding.stepLabels.setup,
      blocks: [...contentBlocks(tech1Onboarding.stepContent.setup), { kind: "questions", questions: tech1Questions }],
      prerequisites: [{ stepKey: "introduction", requirement: "visited" }],
    },
    {
      key: "entities",
      label: tech1Onboarding.stepLabels.entities ?? "Choose entities",
      blocks: [
        ...contentBlocks(`### Choose entities

Choose the entities used by this project. The Template uses their names to fill entity and hierarchy paths when it creates the mappings.`),
        // Other setup can continue, but mapping creation needs these names to fill entity paths.
        // The employee entity's contact-entity status sits right under its select, before the other entities.
        { kind: "entities", entities: tech1Entities.filter((entity) => entity.key === "entities.employee"), allowSkip: true },
        employeeContactEntityBlock,
        { kind: "entities", entities: tech1Entities.filter((entity) => entity.key !== "entities.employee"), allowSkip: true },
      ],
      prerequisites: [{ stepKey: "setup", requirement: "complete" }],
    },
    {
      key: "awardSession",
      label: "Choose the award interpretation goal",
      blocks: [
        ...contentBlocks("### Choose the award interpretation goal\n\nSelect the release goal used for each Employee_ID group of approved Technology1 time rows. The group creates one session; successful interpretation can then hand an eligible result to Edward for controlled pay-run collection."),
        {
          kind: "questions",
          questions: [{
            key: "awardSessionGoal",
            label: "Award interpretation goal",
            helpText: "Each valid Employee_ID group creates one session at this goal.",
            type: "attribute",
            required: true,
          }],
          allowSkip: true,
        },
      ],
      showWhen: { mode: "awardInterpretation" },
      prerequisites: [{ stepKey: "entities", requirement: "complete" }],
    },
    ...mappingSteps,
    ...connectionSteps,
    {
      key: "fileProcessingProject",
      label: tech1Onboarding.stepLabels.fileProcessing,
      blocks: [...contentBlocks(tech1Onboarding.stepContent.fileProcessing), { kind: "fileProcessing" }],
      prerequisites: [{ stepKey: "setup", requirement: "visited" }],
    },
    {
      key: "routing",
      label: tech1Onboarding.stepLabels.routing,
      actionLabel: "Apply routes",
      blocks: [
        ...contentBlocks(tech1Onboarding.stepContent.routing),
        { kind: "routing", resources: tech1Routes.map((route) => ({ kind: "route" as const, resource: route })) },
      ],
      prerequisites: [
        {
          stepKey: "fileProcessingProject",
          requirement: "complete",
          reason: "Enable file processing first.",
        },
        {
          stepKey: "awardSession",
          requirement: "complete",
          reason: "Choose the award interpretation goal first.",
        },
      ],
    },
    {
      key: "alerts",
      label: tech1Onboarding.stepLabels.verification,
      blocks: [
        ...contentBlocks(tech1Onboarding.stepContent.verification),
        {
          kind: "connector",
          resources: [{ kind: "connector", resource: {
            key: "processingAlerts",
            name: "Processing failure alerts",
            subscribedEvents: ["fileProcessing.failed", "fileProcessing.failedRows"],
          } }],
        },
      ],
      prerequisites: [{ stepKey: "routing", requirement: "complete" }],
    },
    {
      key: "finish",
      label: tech1Onboarding.stepLabels.finish,
      blocks: endToEndTestBlocks,
      // Alerts are recommended but optional to reach the end: finishing lists them if they aren't set up.
      prerequisites: [{ stepKey: "routing", requirement: "complete" }],
    },
  ],
};
